/**
 * rego-ai-flow `TicketThreadContextBuilder` markdown: ## TICKET ANALYSIS + ### sections
 * @param {string} desc
 */
export function parseTicketAnalysisMarkdown(desc) {
  if (!desc || !desc.includes("## TICKET ANALYSIS")) return null;
  const parts = desc
    .split(/\n(?=### )/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;
  const header = parts[0];
  const sections = parts.slice(1).map((block) => {
    const lines = block.split("\n");
    const title = lines[0]?.replace(/^###\s*/, "").replace(/:$/, "").trim() || "Section";
    const body = lines.slice(1).join("\n").trim();
    return { title, body };
  });

  // Extract ticket metadata from header bold fields
  const ticketMeta = {};
  const metaPattern = /\*\*(.+?)\*\*\s*:?\s*(.+)/g;
  let match;
  while ((match = metaPattern.exec(header)) !== null) {
    const key = match[1].trim().toLowerCase().replace(/\s+/g, "_");
    const val = match[2].trim();
    if (val && val !== "N/A") ticketMeta[key] = val;
  }

  // Parse sections into structured data
  const structured = sections.map((sec) => {
    const titleLower = sec.title.toLowerCase();

    // Custom fields section — parse bold key-value pairs
    if (titleLower.includes("custom field")) {
      const fields = [];
      const fieldPattern = /\*\*(.+?)\*\*\s*:?\s*(.+)/g;
      let fm;
      while ((fm = fieldPattern.exec(sec.body)) !== null) {
        fields.push({ key: fm[1].trim(), value: fm[2].trim() });
      }
      return { ...sec, type: "custom_fields", fields };
    }

    // Primary description — may contain embedded JSON doc
    if (titleLower.includes("primary description") || titleLower.includes("description")) {
      const jsonMatch = sec.body.match(/(\{[\s\S]*\})/);
      if (jsonMatch) {
        try {
          const doc = JSON.parse(jsonMatch[1]);
          return { ...sec, type: "description_doc", doc };
        } catch {
          // not valid JSON, fall through
        }
      }
      return { ...sec, type: "description" };
    }

    // Conversation / attachments
    if (titleLower.includes("conversation") || titleLower.includes("attachment")) {
      return { ...sec, type: "conversation" };
    }

    return { ...sec, type: "text" };
  });

  return { header, sections: structured, ticketMeta };
}

/**
 * Recursively render Atlassian Document Format (ADF) nodes to plain structured elements.
 * Returns an array of { type, text, items?, rows? } for the renderer to consume.
 * @param {Array} content - ADF content array
 * @returns {Array}
 */
export function flattenAdfContent(content) {
  if (!Array.isArray(content)) return [];
  const result = [];

  for (const node of content) {
    switch (node.type) {
      case "paragraph": {
        const text = extractAdfText(node.content);
        if (text) result.push({ type: "paragraph", text });
        break;
      }
      case "bulletList":
      case "orderedList": {
        const items = (node.content || [])
          .filter((li) => li.type === "listItem")
          .map((li) => extractAdfText(li.content));
        if (items.length) result.push({ type: node.type, items });
        break;
      }
      case "heading": {
        const text = extractAdfText(node.content);
        const level = node.attrs?.level || 3;
        if (text) result.push({ type: "heading", text, level });
        break;
      }
      case "codeBlock": {
        const text = extractAdfText(node.content);
        if (text) result.push({ type: "codeBlock", text });
        break;
      }
      case "blockquote": {
        const inner = flattenAdfContent(node.content);
        result.push({ type: "blockquote", children: inner });
        break;
      }
      case "table": {
        const rows = (node.content || []).map((row) =>
          (row.content || []).map((cell) => extractAdfText(cell.content))
        );
        if (rows.length) result.push({ type: "table", rows });
        break;
      }
      case "mediaGroup": {
        result.push(...flattenAdfContent(node.content || []));
        break;
      }
      case "media": {
        const attrs = node.attrs || {};
        const id = attrs.id != null ? String(attrs.id) : "";
        const mediaType = attrs.type || "file";
        result.push({
          type: "mediaAttachment",
          mediaType,
          id,
          label:
            mediaType === "file"
              ? id
                ? `Attached file · ${id}`
                : "Attached file"
              : `Attachment (${mediaType})`,
        });
        break;
      }
      default: {
        // Recurse into unknown container nodes
        if (node.content) {
          result.push(...flattenAdfContent(node.content));
        }
        break;
      }
    }
  }
  return result;
}

/**
 * Extract plain text from ADF inline content array (paragraphs, list items, etc.)
 * Handles nested paragraphs inside list items.
 */
function extractAdfText(content) {
  if (!Array.isArray(content)) return "";
  return content
    .map((c) => {
      if (c.type === "text") return c.text || "";
      if (c.type === "hardBreak") return "\n";
      if (c.type === "paragraph") return extractAdfText(c.content);
      if (c.type === "mention") return c.attrs?.text || "@someone";
      if (c.type === "emoji") return c.attrs?.shortName || "";
      if (c.content) return extractAdfText(c.content);
      return "";
    })
    .join("");
}
